import { Plus } from "lucide-react";
import * as React from "react";
import { DataGridColumnHeader } from "@/components/data-grid/data-grid-column-header";
import { DataGridContextMenu } from "@/components/data-grid/data-grid-context-menu";
import { DataGridPasteDialog } from "@/components/data-grid/data-grid-paste-dialog";
import { DataGridRow } from "@/components/data-grid/data-grid-row";
import { DataGridSearch } from "@/components/data-grid/data-grid-search";
import { flexRender, getCommonPinningStyles } from "@/lib/data-grid";
import { cn } from "@/lib/utils/index";

const EMPTY_CELL_SELECTION_SET = new Set();

export function DataGrid(
  {
    dataGridRef,
    headerRef,
    rowMapRef,
    footerRef,
    dir = "ltr",
    table,
    tableMeta,
    virtualTotalSize,
    virtualItems,
    measureElement,
    columns,
    columnSizeVars,
    searchState,
    searchMatchesByRow,
    activeSearchMatch,
    cellSelectionMap,
    focusedCell,
    editingCell,
    rowHeight,
    contextMenu,
    pasteDialog,
    onRowAdd,
    height = 600,
    stretchColumns = false,
    className,
    ...props
  }
) {
  const rows = table.getRowModel().rows;
  const readOnly = tableMeta?.readOnly ?? false;
  const columnVisibility = table.getState().columnVisibility;
  const columnPinning = table.getState().columnPinning;

  const onDataGridContextMenu = React.useCallback((event) => {
    event.preventDefault()
    event.stopPropagation?.()

    // Read coords immediately (safe w/ React events) and ask tableMeta to open the menu.
    const x = event.clientX
    const y = event.clientY
    tableMeta?.onOpenContextMenu?.({ x, y })
  }, [tableMeta])

  const onAddRowKeyDown = React.useCallback((event) => {
    if (!onRowAdd) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowAdd();
    }
  }, [onRowAdd]);

  return (
    <div
      data-slot="grid-wrapper"
      dir={dir}
      {...props}
      className={cn("relative flex w-full flex-col", className)}>
      {searchState && <DataGridSearch {...searchState} />}
      <DataGridPasteDialog tableMeta={tableMeta} pasteDialog={pasteDialog} />
      <div
        role="grid"
        aria-label="Data grid"
        aria-rowcount={rows.length + (onRowAdd ? 1 : 0)}
        aria-colcount={columns.length}
        data-slot="grid"
        tabIndex={0}
        ref={dataGridRef}
        className="relative grid select-none overflow-auto rounded-md border bg-background/50 focus:outline-none"
        style={{
          ...columnSizeVars,
          maxHeight: `${height}px`,
        }}
        onContextMenu={onDataGridContextMenu}>
      <DataGridContextMenu tableMeta={tableMeta} columns={columns} contextMenu={contextMenu} />
        <div
          role="rowgroup"
          data-slot="grid-header"
          ref={headerRef}
          className="sticky top-0 z-10 grid border-b bg-gradient-to-b from-background via-background/95 to-background/80 backdrop-blur">
          {table.getHeaderGroups().map((headerGroup, rowIndex) => {
            let ariaColIndex = 0;

            return (
              <div
                key={headerGroup.id}
                role="row"
                aria-rowindex={rowIndex + 1}
                data-slot="grid-header-row"
                tabIndex={-1}
                className="flex w-full">
                {headerGroup.headers.map((header) => {
                  const currentIndex = ariaColIndex;
                  ariaColIndex += header.colSpan ?? 1;

                  const sorting = table.getState().sorting;
                  const currentSort = sorting.find((sort) => sort.id === header.column.id);
                  const isSortable = header.column.getCanSort();

                  return (
                    <div
                      key={header.id}
                      role="columnheader"
                      aria-colindex={currentIndex + 1}
                      aria-colspan={header.colSpan > 1 ? header.colSpan : undefined}
                      aria-sort={
                        currentSort?.desc === false
                          ? "ascending"
                          : currentSort?.desc === true
                            ? "descending"
                            : isSortable
                              ? "none"
                              : undefined
                      }
                      data-slot="grid-header-cell"
                      tabIndex={-1}
                      className={cn("relative shrink-0", {
                        grow: stretchColumns && header.column.id !== "select",
                        "border-e": header.column.id !== "select",
                      })}
                      style={{
                        ...getCommonPinningStyles({ column: header.column, dir, withBorder: true }),
                        width: `calc(var(--header-${header.id}-size) * 1px)`,
                      }}>
                    {header.isPlaceholder ? null : typeof header.column
                        .columnDef.header === "function" ? (
                      <div className="size-full px-3 py-1.5">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    ) : (
                      <DataGridColumnHeader header={header} table={table} />
                    )}
                  </div>
                );
              })}
            </div>
          )})}
        </div>
        <div
          role="rowgroup"
          data-slot="grid-body"
          className="relative grid"
          style={{
            height: `${virtualTotalSize}px`,
            contain: "strict",
          }}>
          {virtualItems.map((virtualItem) => {
            const row = rows[virtualItem.index];
            if (!row) return null;

            const cellSelectionKeys =
              cellSelectionMap?.get(virtualItem.index) ??
              EMPTY_CELL_SELECTION_SET;

            const searchMatchColumns =
              searchMatchesByRow?.get(virtualItem.index) ?? null;
            const isActiveSearchRow =
              activeSearchMatch?.rowIndex === virtualItem.index;

            return (
              <DataGridRow
                key={row.id}
                row={row}
                tableMeta={tableMeta}
                rowMapRef={rowMapRef}
                virtualItem={virtualItem}
                measureElement={measureElement}
                rowHeight={rowHeight}
                columnVisibility={columnVisibility}
                columnPinning={columnPinning}
                focusedCell={focusedCell}
                editingCell={editingCell}
                cellSelectionKeys={cellSelectionKeys}
                searchMatchColumns={searchMatchColumns}
                activeSearchMatch={isActiveSearchRow ? activeSearchMatch : null}
                dir={dir}
                readOnly={readOnly}
                stretchColumns={stretchColumns} />
            );
          })}
        </div>
        {onRowAdd && (
          <div
            role="rowgroup"
            data-slot="grid-footer"
            ref={footerRef}
            className="sticky bottom-0 z-10 grid border-t bg-background">
            <div
              role="row"
              aria-rowindex={rows.length + 2}
              data-slot="grid-add-row"
              tabIndex={-1}
              className="flex w-full">
              <div
                role="gridcell"
                tabIndex={0}
                className="relative flex h-9 grow items-center bg-muted/30 transition-colors hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
                style={{
                  width: table.getTotalSize(),
                  minWidth: table.getTotalSize(),
                }}
                onClick={onRowAdd}
                onKeyDown={onAddRowKeyDown}>
                <div
                  className="sticky start-0 flex items-center gap-2 px-3 text-muted-foreground">
                  <Plus className="size-3.5" />
                  <span className="text-sm">Add row</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
